import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash';
import { GridColumnResizeParams } from '@mui/x-data-grid-pro';

const STORAGE_KEY_PREFIX = 'flowing_datagrid_v1_';

export interface GridPersistenceState {
    columnOrder: string[];
    columnWidths: Record<string, number>;
    pageSize: number;
}

interface UseGridPersistenceOptions {
    persistenceKey?: string;
    defaultPageSize?: number;
}

interface UseGridPersistenceReturn {
    // 저장된 상태
    savedState: GridPersistenceState | null;
    // 이벤트 핸들러
    handleColumnOrderChange: (newOrder: string[]) => void;
    handleColumnWidthChange: (params: GridColumnResizeParams) => void;
    handlePageSizeChange: (pageSize: number) => void;
    // persistenceKey 존재 여부
    isPersistenceEnabled: boolean;
}

const getStorageKey = (persistenceKey: string) => `${STORAGE_KEY_PREFIX}${persistenceKey}`;

/**
 * localStorage에서 저장된 pageSize를 가져오는 헬퍼 함수
 * 페이지 컴포넌트에서 limit 초기값 설정 시 사용
 */
export const getSavedPageSize = (persistenceKey: string, defaultPageSize = 15): number => {
    try {
        const key = getStorageKey(persistenceKey);
        const saved = localStorage.getItem(key);
        if (saved) {
            const parsed = JSON.parse(saved) as GridPersistenceState;
            return parsed.pageSize || defaultPageSize;
        }
    } catch (e) {
        console.error('Failed to get saved page size:', e);
    }
    return defaultPageSize;
};

const loadState = (persistenceKey: string): GridPersistenceState | null => {
    try {
        const key = getStorageKey(persistenceKey);
        const saved = localStorage.getItem(key);
        if (saved) {
            return JSON.parse(saved) as GridPersistenceState;
        }
    } catch (e) {
        console.error('Failed to load grid state:', e);
    }
    return null;
};

const saveState = (persistenceKey: string, state: GridPersistenceState) => {
    try {
        const key = getStorageKey(persistenceKey);
        localStorage.setItem(key, JSON.stringify(state));
    } catch (e) {
        console.error('Failed to save grid state:', e);
    }
};

export const useGridPersistence = ({
    persistenceKey,
    defaultPageSize = 25,
}: UseGridPersistenceOptions): UseGridPersistenceReturn => {
    const isPersistenceEnabled = !!persistenceKey;

    // 초기 상태 로드
    const [savedState, setSavedState] = useState<GridPersistenceState | null>(() => {
        if (!persistenceKey) return null;
        return loadState(persistenceKey);
    });

    // 현재 상태를 추적하기 위한 ref
    const currentStateRef = useRef<GridPersistenceState>({
        columnOrder: savedState?.columnOrder || [],
        columnWidths: savedState?.columnWidths || {},
        pageSize: savedState?.pageSize || defaultPageSize,
    });

    // debounced save 함수
    const debouncedSave = useMemo(() => {
        if (!persistenceKey) return () => {};
        return debounce((state: GridPersistenceState) => {
            saveState(persistenceKey, state);
            setSavedState(state);
        }, 300);
    }, [persistenceKey]);

    // 컴포넌트 언마운트 시 debounce flush
    useEffect(() => {
        return () => {
            if (debouncedSave && 'flush' in debouncedSave && typeof debouncedSave.flush === 'function') {
                debouncedSave.flush();
            }
        };
    }, [debouncedSave]);

    // 열 순서 변경 핸들러 - 새로운 순서 배열을 직접 받음
    const handleColumnOrderChange = useCallback(
        (newOrder: string[]) => {
            if (!persistenceKey) return;

            currentStateRef.current = {
                ...currentStateRef.current,
                columnOrder: newOrder,
            };

            debouncedSave(currentStateRef.current);
        },
        [persistenceKey, debouncedSave],
    );

    // 열 너비 변경 핸들러
    const handleColumnWidthChange = useCallback(
        (params: GridColumnResizeParams) => {
            if (!persistenceKey) return;

            const { colDef, width } = params;

            currentStateRef.current = {
                ...currentStateRef.current,
                columnWidths: {
                    ...currentStateRef.current.columnWidths,
                    [colDef.field]: width,
                },
            };

            debouncedSave(currentStateRef.current);
        },
        [persistenceKey, debouncedSave],
    );

    // 페이지 사이즈 변경 핸들러
    const handlePageSizeChange = useCallback(
        (pageSize: number) => {
            if (!persistenceKey) return;

            currentStateRef.current = {
                ...currentStateRef.current,
                pageSize,
            };

            // 페이지 사이즈는 즉시 저장
            saveState(persistenceKey, currentStateRef.current);
            setSavedState({ ...currentStateRef.current });
        },
        [persistenceKey],
    );

    // currentStateRef 초기화 (savedState 변경 시)
    useEffect(() => {
        if (savedState) {
            currentStateRef.current = { ...savedState };
        }
    }, [savedState]);

    return {
        savedState,
        handleColumnOrderChange,
        handleColumnWidthChange,
        handlePageSizeChange,
        isPersistenceEnabled,
    };
};

export default useGridPersistence;
